// Tablero tributario. Contabilidad → Obligaciones Tributarias.
//
// El 104 y el 103 ya existían, pero se miran el día 9 del mes siguiente, cuando
// ya no se puede hacer nada. Esta pantalla los muestra del mes EN CURSO, contra
// el mes anterior, con la fecha límite real de la empresa y —lo que de verdad
// cambia el trabajo— avisos de lo que falta corregir mientras aún hay margen.
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, ArrowRight, CalendarClock, CheckCircle2, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { useTabsStore } from '@/stores/tabs.store';
import { field, rpc, usd } from './shared';

const MESES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

interface Aviso {
  clave: string; severidad: 'alta' | 'media'; titulo: string; detalle?: string;
  cantidad?: number; importe?: number; ruta?: string;
}
interface Tablero {
  anio: number; mes: number; ruc: string | null; noveno_digito: string | null;
  contribuyente_especial: boolean; fecha_limite: string; dias_restantes: number;
  periodo_anterior: { anio: number; mes: number };
  f104: Record<string, any>; f104_anterior: Record<string, any>;
  f103: { total_base: number; total_retenido: number };
  avisos: Aviso[];
}

const n2 = (v: unknown) => Number(v ?? 0);

// Una cifra con su comparación contra el mes anterior. La variación va en
// palabras además de en color: «+12 % sobre junio» dice más que una flecha.
function Cifra({ titulo, valor, anterior, mesAnterior, destacar }: {
  titulo: string; valor: number; anterior: number; mesAnterior: string; destacar?: boolean;
}) {
  const dif = valor - anterior;
  const pct = anterior !== 0 ? (dif / Math.abs(anterior)) * 100 : null;
  return (
    <div className={`rounded-lg border p-3 ${destacar ? 'border-primary/50 bg-primary/5' : ''}`}>
      <div className="text-xs text-muted-foreground">{titulo}</div>
      <div className={`font-mono tabular-nums ${destacar ? 'text-xl font-semibold' : 'text-lg'}`}>{usd(valor)}</div>
      <div className="mt-0.5 text-xs text-muted-foreground">
        {anterior === 0 && valor === 0 ? 'sin movimiento' : (
          <>
            {dif === 0 ? 'igual que' : (
              <span className={dif > 0 ? 'text-amber-600' : 'text-green-600'}>
                {dif > 0 ? '+' : ''}{usd(dif)}{pct !== null && ` (${pct > 0 ? '+' : ''}${pct.toFixed(0)} %)`}
              </span>
            )} sobre {mesAnterior}
          </>
        )}
      </div>
    </div>
  );
}

export function TableroTributario() {
  const hoy = new Date();
  const [anio, setAnio] = useState(hoy.getFullYear());
  const [mes, setMes] = useState(hoy.getMonth() + 1);
  const openTab = useTabsStore((s) => s.openTab);

  const { data, isFetching } = useQuery<Tablero>({
    queryKey: ['trib-tablero', anio, mes],
    queryFn: () => rpc<Tablero>('trib_tablero', { _anio: anio, _mes: mes }),
  });

  const f = data?.f104 ?? {}; const fp = data?.f104_anterior ?? {};
  const mesAnt = data ? MESES[data.periodo_anterior.mes - 1].toLowerCase() : '';
  const dias = data?.dias_restantes ?? 0;
  const vencido = dias < 0;
  const avisos = data?.avisos ?? [];

  return (
    <div className="p-6 space-y-4">
      <h1 className="text-xl font-semibold">Tablero Tributario</h1>
      <p className="text-sm text-muted-foreground">
        El IVA y las retenciones del período, comparados con el mes anterior, con lo que falta corregir antes de
        declarar. Las cifras son las mismas del Formulario 104; lo que cambia es cuándo las ves.
      </p>

      <Card><CardContent className="flex flex-wrap items-end gap-3 pt-4">
        <label className="flex flex-col text-xs text-muted-foreground">Año
          <input type="number" className={`${field} w-24 mt-1`} value={anio}
            onChange={(e) => setAnio(Number(e.target.value) || anio)} /></label>
        <label className="flex flex-col text-xs text-muted-foreground">Mes
          <select className={`${field} w-36 mt-1`} value={mes} onChange={(e) => setMes(Number(e.target.value))}>
            {MESES.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select></label>
        {isFetching && <Loader2 className="w-4 h-4 animate-spin text-primary mb-2" />}
      </CardContent></Card>

      {data && (
        <>
          {/* El plazo, que hasta ahora sólo estaba escrito en la Ayuda */}
          <Card className={vencido ? 'border-red-500/50' : dias <= 5 ? 'border-amber-500/50' : ''}>
            <CardContent className="flex flex-wrap items-center gap-x-6 gap-y-2 pt-4">
              <CalendarClock className={`w-5 h-5 ${vencido ? 'text-red-600' : dias <= 5 ? 'text-amber-600' : 'text-muted-foreground'}`} />
              <div>
                <div className="text-xs text-muted-foreground">Vence</div>
                <div className="font-medium">{data.fecha_limite}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">
                  {data.contribuyente_especial ? 'Contribuyente especial' : `Noveno dígito del RUC: ${data.noveno_digito ?? '—'}`}
                </div>
                <div className={`font-medium ${vencido ? 'text-red-600' : dias <= 5 ? 'text-amber-600' : ''}`}>
                  {vencido ? `Venció hace ${Math.abs(dias)} día(s)` : dias === 0 ? 'Vence hoy' : `Faltan ${dias} día(s)`}
                </div>
              </div>
              {data.ruc && <div className="text-xs text-muted-foreground font-mono">RUC {data.ruc}</div>}
            </CardContent>
          </Card>

          {/* Avisos: lo que hace útil al tablero */}
          {avisos.length === 0 ? (
            <Card><CardContent className="flex items-center gap-2 py-4 text-sm text-green-600">
              <CheckCircle2 className="w-4 h-4" /> No hay nada pendiente de corregir en este período.
            </CardContent></Card>
          ) : (
            <div className="space-y-2">
              {avisos.map((a) => (
                <div key={a.clave}
                  className={`flex flex-wrap items-start gap-3 rounded-lg border p-3 ${
                    a.severidad === 'alta' ? 'border-red-500/40 bg-red-500/5' : 'border-amber-500/40 bg-amber-500/5'}`}>
                  <AlertTriangle className={`mt-0.5 w-4 h-4 shrink-0 ${a.severidad === 'alta' ? 'text-red-600' : 'text-amber-600'}`} />
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-sm">{a.titulo}</p>
                    {a.detalle && <p className="text-xs text-muted-foreground mt-0.5">{a.detalle}</p>}
                    {a.importe != null && <p className="text-xs font-mono tabular-nums mt-0.5">{usd(a.importe)}</p>}
                  </div>
                  {a.ruta && (
                    <button onClick={() => openTab({ id: a.ruta!, title: a.titulo.slice(0, 30), path: a.ruta! })}
                      className="inline-flex items-center gap-1 text-sm text-primary hover:underline shrink-0">
                      Ir <ArrowRight className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">
              IVA · {MESES[data.mes - 1]} {data.anio}
            </CardTitle></CardHeader>
            <CardContent className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <Cifra titulo="Ventas gravadas" valor={n2(f.ventas_gravadas)} anterior={n2(fp.ventas_gravadas)} mesAnterior={mesAnt} />
              <Cifra titulo="Ventas 0 % / exentas" valor={n2(f.ventas_exentas)} anterior={n2(fp.ventas_exentas)} mesAnterior={mesAnt} />
              <Cifra titulo="IVA en ventas" valor={n2(f.iva_ventas)} anterior={n2(fp.iva_ventas)} mesAnterior={mesAnt} />
              <Cifra titulo="Base de compras" valor={n2(f.compras_base)} anterior={n2(fp.compras_base)} mesAnterior={mesAnt} />
              <Cifra titulo="IVA en compras (crédito)" valor={n2(f.iva_compras)} anterior={n2(fp.iva_compras)} mesAnterior={mesAnt} />
              <Cifra titulo="IVA resultante" valor={n2(f.iva_resultante)} anterior={n2(fp.iva_resultante)} mesAnterior={mesAnt} destacar />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">
              Retenciones en la fuente · {MESES[data.mes - 1]}
            </CardTitle></CardHeader>
            <CardContent className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <div className="rounded-lg border p-3">
                <div className="text-xs text-muted-foreground">Base sobre la que se retuvo</div>
                <div className="font-mono tabular-nums text-lg">{usd(n2(data.f103?.total_base))}</div>
              </div>
              <div className="rounded-lg border p-3">
                <div className="text-xs text-muted-foreground">Total retenido (F103)</div>
                <div className="font-mono tabular-nums text-lg">{usd(n2(data.f103?.total_retenido))}</div>
              </div>
              <div className="rounded-lg border p-3">
                <div className="text-xs text-muted-foreground">Retención de IVA que me practicaron</div>
                <div className="font-mono tabular-nums text-lg">{usd(n2(f.retencion_iva_efectuada))}</div>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
